import { useState } from "react";
import { Link } from "react-router-dom";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const FORM_ID = "about-feedback-form";
const FORM_ACTION = "https://readdy.ai/api/form/db9lfqbk13ra8mig95ig";

const contactChannels = [
  {
    icon: "ri-mail-line",
    label: "内容与版权",
    value: "hello@bitbi.video",
    hint: "下架申请、授权合作优先处理",
  },
  {
    icon: "ri-customer-service-2-line",
    label: "用户反馈",
    value: "support@bitbi.video",
    hint: "功能建议与使用问题",
  },
  {
    icon: "ri-wechat-line",
    label: "官方社群",
    value: "逼特逼视频观影群",
    hint: "每周更新片单与话题",
  },
];

const values = [
  {
    icon: "ri-compass-3-line",
    title: "内容清楚可找",
    text: "三大频道、十五个细分题材，加上多维筛选，把找片这件事缩短到几步之内。",
  },
  {
    icon: "ri-eye-line",
    title: "信息完整一致",
    text: "列表与详情共用同一份内容数据，标题、类型、时长、导演与主演不会各说各话。",
  },
  {
    icon: "ri-device-line",
    title: "界面轻量舒适",
    text: "深色界面减少长时间浏览的视觉疲劳，手机端单列布局与放大点击区域更好用。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useDocumentMeta({
    title: "关于我们 - 逼特逼视频",
    description:
      "了解逼特逼视频的定位与内容范围：聚焦热门电影、电视剧与丰富短视频，提供清晰的分类与搜索体验。欢迎通过反馈表单联系我们。",
    keywords: "关于逼特逼视频,视频平台介绍,联系方式,内容反馈",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const honeypot = String(formData.get("website_alt") ?? "").trim();

    setStatus("idle");
    setFormError("");

    if (honeypot) {
      formEl.reset();
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setSubmitting(true);
    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === "OK") {
        formEl.reset();
        setStatus("success");
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交没有成功，请稍后再试一次。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，反馈未能送出，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="relative overflow-hidden border-b border-background-200 bg-background-100">
        <div className="pointer-events-none absolute -right-20 top-0 flex h-80 w-80 items-center justify-center rounded-full bg-primary-500/10 blur-2xl" />
        <div className="page-shell relative py-12 md:py-16">
          <p className="mb-3 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-primary-400">
            <span className="h-px w-6 bg-primary-500" />
            About us
          </p>
          <h1 className="max-w-3xl font-heading text-3xl font-semibold leading-tight tracking-tight text-foreground-950 md:text-[40px]">
            关于逼特逼视频
            <br />
            <span className="text-foreground-600">把想看的内容，放在伸手就能拿到的地方</span>
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
            逼特逼视频是一个面向普通观众的在线视频内容平台，内容覆盖热门电影、口碑电视剧与多样短视频。
            我们不做复杂的会员体系，也不用层层弹窗打断观看，只专注把内容整理清楚、把入口做得顺手，
            让不同年龄段的用户都能轻松找到并了解一部作品。
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/category/movie"
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              浏览电影频道
              <i className="ri-arrow-right-line text-base" />
            </Link>
            <a
              href={`#${FORM_ID}`}
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-400"
            >
              提交反馈
            </a>
          </div>
        </div>
      </section>

      <section className="page-shell py-10 md:py-14">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.15fr_1fr] lg:gap-10">
          <div>
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              我们在做一件什么样的事
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              平台目前收录电影、电视剧、短视频三大频道，并进一步细分为动作、剧情、科幻、悬疑、喜剧、爱情、
              都市、古装、家庭、搞笑、美食、旅行、生活与音乐等题材。每条内容都配有一致的元信息：
              类型、地区、年份、播放时长或集数、导演与主演，方便你在打开详情前就判断这部作品是否合口味。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              为了适配不同场景，桌面端采用门户式布局与多列卡片，手机端则改为单列卡片、折叠导航与弹出式筛选，
              减少横向滚动与误触。深色背景与暖橙主色是我们希望传达的气质：安静、专注，但保留一点活力。
            </p>

            <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {values.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-200 bg-background-100 p-4"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded bg-primary-500/15 text-primary-400">
                    <i className={`${item.icon} text-base`} />
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-medium text-foreground-900">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-base font-medium text-foreground-900">联系方式</h2>
              <p className="mt-1.5 text-xs text-foreground-500">
                内容合作、版权申诉或产品建议，都可以直接联系我们。
              </p>
              <ul className="mt-5 space-y-4">
                {contactChannels.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-background-200 bg-background-50 text-primary-400">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-500">{item.label}</p>
                      <p className="mt-0.5 truncate text-sm text-foreground-900">{item.value}</p>
                      <p className="mt-0.5 text-[11px] text-foreground-500">{item.hint}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-md border border-background-200 bg-background-50 p-3 text-[11px] leading-relaxed text-foreground-500">
                <i className="ri-time-line mr-1 text-primary-400" />
                工作日 10:00 - 19:00，我们通常在 1 个工作日内回复。
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-medium text-foreground-900">版权说明</h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                本站为视频内容浏览演示站点，不存储、不传播任何真实影视片源，播放按钮仅展示模拟播放器界面。
                如内容信息涉及权益问题，请通过上方邮箱与我们联系，我们会尽快核实处理。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-10 md:py-14">
        <div className="page-shell">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.15fr] lg:gap-10">
            <div>
              <p className="mb-2 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-primary-400">
                <span className="h-px w-6 bg-primary-500" />
                Feedback
              </p>
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                告诉我们你的想法
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                想看的片子没找到？分类不够顺手？播放器还有改进空间？
                填写下面的表单，我们会认真读完每一条反馈，并把它用到下一版里。
              </p>
              <ul className="mt-5 space-y-2.5 text-sm text-foreground-600">
                {[
                  "内容建议：希望增加的电影、剧集或短视频",
                  "功能反馈：搜索、筛选、分页的使用体验",
                  "版权与下架：提供作品名称与权益证明即可",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary-500/15 text-primary-400">
                      <i className="ri-check-line text-[10px]" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <form
              id={FORM_ID}
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="feedback-name" className="mb-1.5 block text-xs text-foreground-600">
                    昵称
                  </label>
                  <input
                    id="feedback-name"
                    name="name"
                    type="text"
                    required
                    maxLength={40}
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="feedback-email" className="mb-1.5 block text-xs text-foreground-600">
                    邮箱
                  </label>
                  <input
                    id="feedback-email"
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    placeholder="用于接收回复"
                    className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="feedback-topic" className="mb-1.5 block text-xs text-foreground-600">
                  反馈类型
                </label>
                <select
                  id="feedback-topic"
                  name="topic"
                  defaultValue="内容建议"
                  className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="功能反馈">功能反馈</option>
                  <option value="播放问题">播放问题</option>
                  <option value="版权与下架">版权与下架</option>
                  <option value="商务合作">商务合作</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="feedback-message" className="mb-1.5 block text-xs text-foreground-600">
                  反馈内容
                </label>
                <textarea
                  id="feedback-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请尽量描述清楚，最多 500 字"
                  className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                />
                <p className="mt-1 text-right text-[11px] text-foreground-500">最多 500 字</p>
              </div>

              <div className="form-honeypot" aria-hidden="true">
                <label htmlFor="feedback-website-alt">请勿填写此栏</label>
                <input
                  id="feedback-website-alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base" />
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-base" />
                    提交反馈
                  </>
                )}
              </button>

              {status === "success" && (
                <div className="mt-4 flex items-start gap-2 rounded-md border border-primary-400/40 bg-primary-500/10 p-3 text-xs leading-relaxed text-primary-400">
                  <i className="ri-checkbox-circle-line text-base" />
                  反馈已提交，感谢你的建议！我们会尽快查看并回复。
                </div>
              )}

              {status === "error" && (
                <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-400/40 bg-accent-500/10 p-3 text-xs leading-relaxed text-accent-300">
                  <i className="ri-error-warning-line text-base" />
                  {formError}
                </div>
              )}

              <p className="mt-3 text-[11px] leading-relaxed text-foreground-500">
                提交即表示同意我们仅将以上信息用于处理本次反馈。
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}